import React from 'react';

import { useHistory } from 'react-router';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { ValidationRuleTemplateCategory } from '@recurrency/core-api-schema/dist/common/enums';
import { ValidationRuleTemplateDTO } from '@recurrency/core-api-schema/dist/validationRules/getValidationRuleTemplates';
import { theme } from 'theme';

import { Button } from 'components/Button';
import { FlexSpace } from 'components/FlexSpace';
import { Modal } from 'components/Modal';
import { Typography } from 'components/Typography';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { routes } from 'utils/routes';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { track, TrackEvent } from 'utils/track';

import { TemplateCard } from '../TemplateCard';
import { TemplateCategoryRow } from '../TemplateCategoryRow';
import { getTemplateCategoryDescription, getTemplateCategoryTitle } from '../utils';
import { CustomRuleRequestPanel } from './CustomRuleRequestPanel';

export interface NewValidationRuleFromTemplateModalProps {
  onClose: () => void;
}

export const NewValidationRuleFromTemplateModal = ({ onClose }: NewValidationRuleFromTemplateModalProps) => {
  const { activeTenant, activeUser } = useGlobalApp();
  const [selectedCategory, setSelectedCategory] = React.useState<ValidationRuleTemplateCategory>(
    ValidationRuleTemplateCategory.MostPopular,
  );
  const [selectedTemplate, setSelectedTemplate] = React.useState<ValidationRuleTemplateDTO>();
  const [showCustomRuleRequestPanel, setShowCustomRuleRequestPanel] = React.useState(false);
  const [customRuleData, setCustomRuleData] = React.useState({
    ruleName: '',
    businessProblem: '',
    triggerConditions: '',
    additionalRequirements: '',
  });
  const [isSubmittingCustomRule, setIsSubmittingCustomRule] = React.useState(false);
  const { data: templates } = useCoreApi(schemas.validationRules.getRuleTemplates, {});
  const history = useHistory();

  const handleCustomRuleDataChange = (field: string, value: string) => {
    setCustomRuleData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const categorizedTemplates: Record<ValidationRuleTemplateCategory, ValidationRuleTemplateDTO[]> =
    React.useMemo(() => {
      const categories = Object.values(ValidationRuleTemplateCategory).reduce((acc, key) => {
        acc[key as ValidationRuleTemplateCategory] = [];
        return acc;
      }, {} as Record<ValidationRuleTemplateCategory, ValidationRuleTemplateDTO[]>);

      (templates?.items || []).forEach((temp: ValidationRuleTemplateDTO) => {
        const { category, isMostPopular } = temp;
        // Add most popular to the most popular category, doesn't exclude them from other categories
        if (isMostPopular) {
          categories[ValidationRuleTemplateCategory.MostPopular].push(temp);
        }
        // Skip all items without a category
        if (!category) return;
        if (!categories[category]) {
          categories[category] = [];
        }
        categories[category].push(temp);
      });
      Object.values(categories).forEach((categoryArray) => {
        categoryArray.sort((a, b) => new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime());
      });
      return categories;
    }, [templates]);

  const submitTemplate = async () => {
    if (!selectedTemplate) {
      return;
    }
    const ruleCreationNotification = createSubmissionNotification({
      entityName: 'Rule',
      submittingMessage: `Creating draft rule from template ${selectedTemplate.name}`,
      expectedWaitSeconds: 5,
    });

    // Create the rule, if there is an error, show a notification and return
    let result;
    try {
      result = await coreApiFetch(schemas.validationRules.createValidationRule, {
        bodyParams: { templateId: selectedTemplate.templateId },
      });
    } catch (err) {
      ruleCreationNotification.error(err);
    }
    if (result?.status !== 200) return;

    track(TrackEvent.ValidationRules_CreateTemplatedRule, {
      templateId: selectedTemplate.templateId,
      ruleId: result.data.ruleId,
    });

    // If the rule was created, show a notification and test the rule
    ruleCreationNotification.inProgress({ description: () => `Rule created, testing rule` });
    try {
      await coreApiFetch(schemas.validationRules.postValidationRuleDeployment, {
        pathParams: {
          ruleId: result.data.ruleId,
        },
        bodyParams: {
          test: true,
        },
      });
      ruleCreationNotification.success({
        description: () => `Rule created and tested successfully`,
      });
    } catch (err) {
      ruleCreationNotification.error(err);
    } finally {
      history.push(routes.ruleBuilder.ruleDetails(result.data.ruleId));
    }
  };

  const handleCustomRuleSubmit = async () => {
    const hasRequiredFields =
      customRuleData.ruleName.trim() &&
      customRuleData.businessProblem.trim() &&
      customRuleData.triggerConditions.trim();

    if (!hasRequiredFields) return;

    setIsSubmittingCustomRule(true);

    const notification = createSubmissionNotification({
      entityName: 'Custom Rule Request',
      submittingMessage: 'Submitting custom rule request',
      expectedWaitSeconds: 1,
    });

    try {
      // Track the custom rule request submission
      track(TrackEvent.ValidationRules_RequestCustomRule, {
        tenantId: activeTenant.id,
        tenantName: activeTenant.name,
        userId: activeUser.id,
        userEmail: activeUser.email,
        ruleName: customRuleData.ruleName,
        businessProblem: customRuleData.businessProblem,
        triggerConditions: customRuleData.triggerConditions,
        additionalRequirements: customRuleData.additionalRequirements,
      });

      notification.success({
        successMessage: "Your custom rule request has been submitted successfully. We'll be in touch soon!",
      });

      // Close the modal after successful submission
      onClose();
    } catch (error) {
      console.error('Error submitting custom rule request:', error);
      notification.error(error);
    } finally {
      setIsSubmittingCustomRule(false);
    }
  };

  return (
    <Modal
      title={showCustomRuleRequestPanel ? 'Request Custom Rule' : 'Create new rule'}
      footer={
        showCustomRuleRequestPanel
          ? [
              <Button key="back" onClick={() => setShowCustomRuleRequestPanel(false)}>
                Back
              </Button>,
              <Button
                type="primary"
                key="submit"
                disabled={
                  !(
                    customRuleData.ruleName.trim() &&
                    customRuleData.businessProblem.trim() &&
                    customRuleData.triggerConditions.trim()
                  ) || isSubmittingCustomRule
                }
                onClick={handleCustomRuleSubmit}
                loading={isSubmittingCustomRule}
              >
                Submit Request
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={() => onClose()}>
                Cancel
              </Button>,
              <Button type="primary" key="create" disabled={!selectedTemplate} onClick={() => submitTemplate()}>
                Create Rule
              </Button>,
            ]
      }
      visible
      onCancel={() => onClose()}
      centered
      width={1200}
      bodyStyle={{ overflowX: 'hidden', overflowY: 'auto', padding: 10, maxHeight: 'calc(100vh - 200px)' }}
    >
      <CustomRuleRequestPanel
        showCustomRuleRequestPanel={showCustomRuleRequestPanel}
        customRuleRequestData={customRuleData}
        onCustomRuleRequestDataChange={handleCustomRuleDataChange}
      >
        <FlexSpace direction="row">
          <div
            className={css`
              width: 25%;
              border-right: 1px solid ${theme.colors.neutral[200]};
              max-height: 400px;
              overflow-y: auto;
              align-self: start;
            `}
          >
            {Object.values(ValidationRuleTemplateCategory).map((category) => (
              <TemplateCategoryRow
                category={category}
                key={category}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
              />
            ))}
          </div>
          <div
            className={css`
              width: 75%;
              height: 100%;
              padding-left: 18px;
              padding-right: 18px;
              margin-bottom: auto;
            `}
          >
            <FlexSpace
              direction="column"
              className={css`
                width: 100%;
                height: 100%;
              `}
            >
              <Typography type="middle" style={{ fontWeight: 'bold' }}>
                {getTemplateCategoryTitle(selectedCategory)}
              </Typography>
              <Typography type="subtitle" style={{ fontWeight: 400, marginBottom: 18 }}>
                {getTemplateCategoryDescription(selectedCategory)}
              </Typography>
              <FlexSpace direction="row" wrap gap={16} alignItems="flex-start">
                {categorizedTemplates[selectedCategory].map((template) => (
                  <TemplateCard
                    key={template.templateId}
                    template={template}
                    selectedTemplate={selectedTemplate}
                    setSelectedTemplate={setSelectedTemplate}
                  />
                ))}
              </FlexSpace>

              {/* Request Custom Rule Section */}
              <div
                className={css`
                  margin-top: 32px;
                  padding: 20px;
                  border: 1px solid ${theme.colors.neutral[300]};
                  border-radius: 8px;
                  transition: all 0.2s ease;
                  width: 100%;
                  cursor: pointer;

                  &:hover {
                    border-color: ${theme.colors.primary[400]};
                    background-color: ${theme.colors.primary[100]};
                  }
                `}
                onClick={() => setShowCustomRuleRequestPanel(true)}
              >
                <FlexSpace direction="row" justify="space-between" alignItems="center">
                  <FlexSpace direction="column" gap={4}>
                    <Typography type="middle" style={{ fontWeight: 600, fontSize: '16px' }}>
                      Request Custom Rule
                    </Typography>
                    <Typography type="subtitle" style={{ fontWeight: 400, color: theme.colors.neutral[600] }}>
                      Can't find what you need? We'll build it.
                    </Typography>
                  </FlexSpace>
                  <Typography
                    type="subtitle"
                    style={{
                      color: theme.colors.primary[500],
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                  >
                    Start Request →
                  </Typography>
                </FlexSpace>
              </div>
            </FlexSpace>
          </div>
        </FlexSpace>
      </CustomRuleRequestPanel>
    </Modal>
  );
};
